/**
 * Cloud Agent — which "what is happening" affordance an assistant turn shows. Pure, React-free
 * (same reasoning as `blocks/blockMeta.ts`: importable from a test without a DOM).
 *
 * Three mutually-exclusive states:
 *  - `activity` — the turn is working and has no prose yet: a ONE-LINE indicator showing the
 *    current step (or "Thinking…" before the first `status` frame), expandable to the full list.
 *  - `cursor`   — prose is streaming: the typing caret carries it, no separate indicator.
 *  - `summary`  — the turn is done and did real work: a muted "Checked N things · 12s" one-liner,
 *    still expandable, so the trail of what the agent actually did stays available.
 *
 * The two answer shapes reach these differently. A `reply` answer streams `text` deltas, so it
 * passes activity → cursor. A `compose_layout` answer streams NO prose (it arrives in
 * `done.answer.text`), so it stays on `activity` for the whole turn and then flips to `summary`.
 */
import type { ChatTurn } from '../../../../stores/appCloudAgentStore';

/** The subset of a turn the rule reads. */
export type TurnActivityInput = Pick<ChatTurn, 'streaming' | 'text' | 'status' | 'error'>;

export interface TurnActivityState {
  showActivity: boolean;
  showCursor: boolean;
  showSummary: boolean;
}

export function turnActivityState(turn: TurnActivityInput): TurnActivityState {
  const none = { showActivity: false, showCursor: false, showSummary: false };
  // A failed turn shows its error and nothing else — no half-finished progress next to it.
  if (turn.error) return none;

  const hasText = turn.text.trim().length > 0;
  if (turn.streaming) {
    return hasText
      ? { showActivity: false, showCursor: true, showSummary: false }
      : { showActivity: true, showCursor: false, showSummary: false };
  }

  // Finished: keep the trail only when there was one. A prose-only answer with no tool steps has
  // nothing to summarise, and "Checked 0 things" would be noise.
  return turn.status.length > 0
    ? { showActivity: false, showCursor: false, showSummary: true }
    : none;
}
